import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EDMS_MACHINE_ALARMS_CATEGORY,
  edmsSignalCategory,
} from 'tf-pulse-shared';
import {
  Alarm,
  AlertGroupFieldsFragment,
  CreateAlarmInput,
  ThresholdMode,
  TriggerMode,
  UpdateAlarmInput,
  useCreateAlarmMutation,
  useCreateAlertGroupMutation,
  useDeleteAlarmMutation,
  useGetAlarmsQuery,
  useGetAlertGroupQuery,
  useUpdateAlarmMutation,
  useUpdateAlertGroupMutation,
} from '../../../gql/generated';
import { useResponsive } from '../../../hooks/useResponsive';
import {
  formatMetricName,
  isMachineAlarmSignal,
} from '../../../utils/formatMetric';
import { ErrorDisplay } from '../../ErrorDisplay';
import { friendlyAlarmSaveError } from './friendlyAlarmSaveError';
import { WizardLayout } from '../../ui/WizardLayout';
import { AddAlarmDialogProvider } from './AddAlarmDialogContext';
import { AlarmChannelsStep } from './AlarmChannelsStep';
import { AlarmConditionsStep } from './AlarmConditionsStep';
import { AlarmDetailsStep } from './AlarmDetailsStep';
import { AlarmSignalStep } from './AlarmSignalStep';
import { AlarmTriggerLogicStep } from './AlarmTriggerLogicStep';
import { AlarmFormData, SignalTriggerSettings } from './context';
import { useAddAlarmDialog } from './useAddAlarmDialog';
import { logger } from '../../../utils/logger';

export interface AddAlarmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMachineIds: string[];
  availableMachines?: Array<{ id: string; displayName: string }>;
  /** The alarm to edit. If provided, the dialog will be in edit mode. */
  editingAlarm?: Alarm | null;
  /** Callback when an alarm is successfully created (receives the new alarm ID) */
  onAlarmCreated?: (alarmId: string) => void;
}

const STEPS = [
  {
    number: 1,
    title: 'Details',
    description: 'Set up alert basics and machine selection',
  },
  { number: 2, title: 'Channels', description: 'Choose where alerts are sent' },
  { number: 3, title: 'Signal selection', description: 'Select a signal' },
  { number: 4, title: 'Trigger', description: 'Define trigger conditions' },
  {
    number: 5,
    title: 'Trigger logic',
    description: 'Configure trigger behavior',
  },
] as const;

const MACHINE_ALARM_STEP_COUNT = STEPS.length - 1;

/**
 * Convert HH:MM:SS time string to minutes
 */
function timeStringToMinutes(timeStr: string): string {
  const parts = timeStr.split(':');
  if (parts.length !== 3) return '5';
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  return String(hours * 60 + minutes);
}

/**
 * Convert an existing Alarm to AlarmFormData for editing
 */
function convertAlarmToFormData(alarm: Alarm): Partial<AlarmFormData> {
  const isMachineAlarm =
    isMachineAlarmSignal({
      unit: alarm.unit,
      signalCategory: alarm.signalCategory,
    }) ||
    (alarm.thresholdMode === ThresholdMode.AboveHigh &&
      alarm.thresholdLow === 0 &&
      alarm.thresholdHigh === 0.5);

  // Determine trigger type and threshold values based on thresholdMode
  let triggerType: 'minimum' | 'maximum' | 'range' = 'minimum';
  let operation: 'greater' | 'less' = 'greater';
  let threshold = '';
  let thresholdMax = '';

  if (alarm.thresholdMode === ThresholdMode.OutsideRange) {
    triggerType = 'range';
    threshold = String(alarm.thresholdLow);
    thresholdMax = String(alarm.thresholdHigh);
  } else if (alarm.thresholdMode === ThresholdMode.BelowLow) {
    triggerType = 'minimum';
    operation = 'less';
    threshold = String(alarm.thresholdLow);
  } else if (alarm.thresholdMode === ThresholdMode.AboveHigh) {
    // convertFormDataToInput maps 'minimum' back to BelowLow, so an AboveHigh
    // alarm read as 'minimum' would invert on save.
    triggerType = 'maximum';
    operation = 'greater';
    thresholdMax = String(alarm.thresholdHigh);
  }

  // Determine trigger logic type and values
  let triggerLogicType: 'percentage' | 'period' | 'spike' = 'percentage';
  let percentageThreshold = '75';
  let evaluationWindow = '5';
  let periodMinutes = '75';
  let numberOfSpikes = '5';

  if (alarm.triggerMode === TriggerMode.Percentage) {
    triggerLogicType = 'percentage';
    percentageThreshold = String(alarm.triggerValue);
    evaluationWindow = timeStringToMinutes(alarm.evalWindowSize);
  } else if (alarm.triggerMode === TriggerMode.Period) {
    triggerLogicType = 'period';
    // triggerValue is in seconds, convert to minutes
    periodMinutes = String(Math.round(alarm.triggerValue / 60));
  } else if (alarm.triggerMode === TriggerMode.Spikes) {
    triggerLogicType = 'spike';
    numberOfSpikes = String(alarm.triggerValue);
    evaluationWindow = timeStringToMinutes(alarm.evalWindowSize);
  }

  const cooldownPeriod = timeStringToMinutes(alarm.cooldownPeriod);

  return {
    name: alarm.name,
    // AlarmMachine.machineId corresponds to Machine.id in the machines list
    // AlarmMachine.id is just the association ID, not the machine ID
    machines:
      alarm.machines
        ?.map((m) => m.machineId)
        .filter((id): id is string => !!id) || [],
    signals: alarm.signal ? [alarm.signal] : [],
    category: alarm.signalCategory || '',
    signalUnit: isMachineAlarm ? 'bool' : alarm.unit || '',
    threshold,
    thresholdMax,
    operation,
    triggerType,
    triggerLogicType,
    percentageThreshold,
    evaluationWindow,
    periodMinutes,
    numberOfSpikes,
    cooldownPeriod,
    multiSignalSettings: {},
    // Channels live on the alert group, never on the alarm, so an alarm alone
    // prefills none. Saving moves such an alarm into a group of its own.
    selectedChannels: [],
    channelSearchQuery: '',
    channelTypeFilters: [],
  };
}

/**
 * Read one member alarm's stored condition back into per-signal settings.
 */
function alarmToSignalSettings(alarm: Alarm): SignalTriggerSettings {
  const fields = convertAlarmToFormData(alarm);

  return {
    triggerType: fields.triggerType ?? 'minimum',
    threshold: fields.threshold ?? '',
    thresholdMax: fields.thresholdMax ?? '',
    triggerLogicType: fields.triggerLogicType ?? 'percentage',
    percentageThreshold: fields.percentageThreshold,
    evaluationWindow: fields.evaluationWindow,
    periodMinutes: fields.periodMinutes,
    numberOfSpikes: fields.numberOfSpikes,
    cooldownPeriod: fields.cooldownPeriod,
    advancedSettingsExpanded: false,
  };
}

/**
 * Rebuild the wizard from an alert group and its member alarms, so editing a
 * grouped alert shows every signal instead of the one row that was clicked.
 *
 * Name, machines and channels come from the group. Each member contributes its
 * own signal and condition.
 */
function convertAlertGroupToFormData(
  group: AlertGroupFieldsFragment,
  members: Alarm[]
): Partial<AlarmFormData> {
  const primary = convertAlarmToFormData(members[0]);

  return {
    ...primary,
    name: group.name,
    signals: members.map((member) => member.signal),
    signalMeta: Object.fromEntries(
      members.map((member) => [
        member.signal,
        {
          category: member.signalCategory || '',
          unit: member.unit || '',
        },
      ])
    ),
    multiSignalSettings: Object.fromEntries(
      members.map((member) => [member.signal, alarmToSignalSettings(member)])
    ),
    selectedChannels: group.notificationChannelIds || [],
  };
}

function isMachineAlarmFormData(formData: AlarmFormData): boolean {
  return isMachineAlarmSignal({
    unit: formData.signalUnit,
    signalCategory: formData.category,
  });
}

function shouldSkipTriggerLogicStep(formData: AlarmFormData): boolean {
  // Multi-signal flow combines trigger and trigger-logic on step 4.
  // Single-signal machine alarms also keep the shortened 4-step flow.
  return (
    formData.signals.length > 1 ||
    (formData.signals.length === 1 && isMachineAlarmFormData(formData))
  );
}

function getNumSteps(formData: AlarmFormData): number {
  return shouldSkipTriggerLogicStep(formData)
    ? MACHINE_ALARM_STEP_COUNT
    : STEPS.length;
}

function hasValidThresholds(
  settings: Pick<
    SignalTriggerSettings,
    'triggerType' | 'threshold' | 'thresholdMax'
  >
): boolean {
  if (settings.triggerType === 'range') {
    return (
      settings.threshold.trim() !== '' && settings.thresholdMax.trim() !== ''
    );
  }
  if (settings.triggerType === 'minimum') {
    return settings.threshold.trim() !== '';
  }
  return settings.thresholdMax.trim() !== '';
}

function hasValidTriggerLogic(
  settings: SignalTriggerSettings,
  advancedSettingsExpanded: boolean
): boolean {
  if (!advancedSettingsExpanded) {
    return true;
  }

  if (settings.triggerLogicType === 'percentage') {
    return (
      (settings.percentageThreshold?.trim() ?? '') !== '' &&
      (settings.evaluationWindow?.trim() ?? '') !== '' &&
      (settings.cooldownPeriod?.trim() ?? '') !== ''
    );
  }
  if (settings.triggerLogicType === 'period') {
    return (
      (settings.periodMinutes?.trim() ?? '') !== '' &&
      (settings.cooldownPeriod?.trim() ?? '') !== ''
    );
  }
  return (
    (settings.numberOfSpikes?.trim() ?? '') !== '' &&
    (settings.evaluationWindow?.trim() ?? '') !== '' &&
    (settings.cooldownPeriod?.trim() ?? '') !== ''
  );
}

/** EDMS caps an alarm name and a group name at 200 characters. */
const NAME_MAX_LENGTH = 200;

/**
 * The alarms list has no by-id filter, so a grouped alert reads enough of it to
 * find its members.
 */
const MEMBER_ALARM_FETCH_LIMIT = 1000;

function buildMemberAlarmName(groupName: string, signal: string): string {
  return `${groupName} - ${formatMetricName(signal)}`.slice(0, NAME_MAX_LENGTH);
}

/**
 * Project the form onto one signal, so the existing threshold and trigger
 * mapping produces one member alarm of a grouped alert.
 *
 * Channels stay off the member. The alert group holds them and delivers one
 * message for the whole group.
 */
function buildMemberFormData(
  formData: AlarmFormData,
  signal: string
): AlarmFormData {
  const isMultiSignal = formData.signals.length > 1;
  // Per-signal settings only apply while the multi-signal step is the one being
  // edited. With one signal the flat fields are what the user just changed, and
  // a stale entry left over from a wider selection would override them.
  const settings = isMultiSignal
    ? formData.multiSignalSettings?.[signal]
    : undefined;
  const meta = formData.signalMeta?.[signal];

  return {
    ...formData,
    // One signal keeps the alert's own name, the name a notification carries.
    // Several signals each take the group name and their signal.
    name: isMultiSignal
      ? buildMemberAlarmName(formData.name, signal)
      : formData.name,
    signals: [signal],
    category: meta?.category ?? formData.category,
    signalUnit: meta?.unit ?? formData.signalUnit,
    // Channels live on the group: EDMS evaluates and notifies groups only
    selectedChannels: [],
    ...(settings
      ? {
          triggerType: settings.triggerType,
          threshold: settings.threshold,
          thresholdMax: settings.thresholdMax,
          triggerLogicType: settings.triggerLogicType,
          percentageThreshold: settings.percentageThreshold,
          evaluationWindow: settings.evaluationWindow,
          periodMinutes: settings.periodMinutes,
          numberOfSpikes: settings.numberOfSpikes,
          cooldownPeriod: settings.cooldownPeriod,
        }
      : {}),
  };
}

/** One CreateAlarmInput per selected signal. */
function buildMemberInputs(formData: AlarmFormData): CreateAlarmInput[] {
  return formData.signals.map((signal) =>
    convertFormDataToInput(buildMemberFormData(formData, signal))
  );
}

/**
 * Convert form data to GraphQL CreateAlarmInput
 * Handles time conversions (minutes to HH:MM:SS format) and threshold modes
 */
function convertFormDataToInput(formData: AlarmFormData): CreateAlarmInput {
  const isMachineAlarm = isMachineAlarmFormData(formData);

  // Helper to convert minutes to HH:MM:SS format
  const minutesToTimeString = (minutes: string): string => {
    const mins = parseInt(minutes, 10);
    if (isNaN(mins)) return '00:00:00';
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${String(hours).padStart(2, '0')}:${String(remainingMins).padStart(2, '0')}:00`;
  };

  // Determine threshold mode based on trigger type
  let thresholdMode: ThresholdMode;
  let thresholdLow = 0;
  let thresholdHigh = 0;

  if (isMachineAlarm) {
    // Machine alarm signals are boolean-like (0/1). Trigger when state is true.
    thresholdMode = ThresholdMode.AboveHigh;
    thresholdLow = 0;
    thresholdHigh = 0.5;
  } else if (formData.triggerType === 'range') {
    thresholdMode = ThresholdMode.OutsideRange;
    thresholdLow = parseFloat(formData.threshold) || 0;
    thresholdHigh = parseFloat(formData.thresholdMax) || 0;
  } else if (formData.triggerType === 'minimum') {
    thresholdMode = ThresholdMode.BelowLow;
    thresholdLow = parseFloat(formData.threshold) || 0;
  } else {
    // maximum
    thresholdMode = ThresholdMode.AboveHigh;
    thresholdHigh = parseFloat(formData.thresholdMax) || 0;
  }

  // Determine trigger mode and value
  let triggerMode: TriggerMode;
  let triggerValue = 0;
  let evalWindowSize = '00:05:00';
  let evalPeriod = '00:05:00';
  let cooldownPeriod = '00:05:00';

  if (isMachineAlarm) {
    // Machine alarm signals are binary (on/off), so always evaluate as spikes.
    triggerMode = TriggerMode.Spikes;
    triggerValue = 1;
    evalWindowSize = minutesToTimeString(formData.evaluationWindow || '1');
    cooldownPeriod = minutesToTimeString(formData.cooldownPeriod || '5');
    evalPeriod = evalWindowSize;
  } else if (formData.triggerLogicType === 'percentage') {
    triggerMode = TriggerMode.Percentage;
    triggerValue = parseFloat(formData.percentageThreshold || '75');
    evalWindowSize = minutesToTimeString(formData.evaluationWindow || '5');
    cooldownPeriod = minutesToTimeString(formData.cooldownPeriod || '5');
    evalPeriod = evalWindowSize; // Use same as eval window
  } else if (formData.triggerLogicType === 'period') {
    triggerMode = TriggerMode.Period;
    // Period is in minutes, convert to seconds for triggerValue
    triggerValue = parseFloat(formData.periodMinutes || '75') * 60;
    cooldownPeriod = minutesToTimeString(formData.cooldownPeriod || '5');
    evalWindowSize = minutesToTimeString(formData.periodMinutes || '75');
    evalPeriod = evalWindowSize;
  } else {
    // spike
    triggerMode = TriggerMode.Spikes;
    triggerValue = parseFloat(formData.numberOfSpikes || '5');
    evalWindowSize = minutesToTimeString(formData.evaluationWindow || '5');
    cooldownPeriod = minutesToTimeString(formData.cooldownPeriod || '5');
    evalPeriod = evalWindowSize;
  }

  // Build the input object
  const input: CreateAlarmInput = {
    name: formData.name,
    machineIds: formData.machines,
    // Use first metric as signal, category defaults to selected category
    signal: formData.signals[0] || '',
    // The category the machine's signal list reports, so EDMS finds the data
    signalCategory:
      edmsSignalCategory(formData.category) ||
      (isMachineAlarm ? EDMS_MACHINE_ALARMS_CATEGORY : ''),
    thresholdLow,
    thresholdHigh,
    thresholdMode,
    triggerMode,
    triggerValue,
    evalWindowSize,
    evalPeriod,
    cooldownPeriod,
    // No channels: the alert group over the alarm carries them
  };

  return input;
}

/**
 * Convert form data to GraphQL UpdateAlarmInput
 */
function convertFormDataToUpdateInput(
  formData: AlarmFormData
): UpdateAlarmInput {
  const createInput = convertFormDataToInput(formData);

  // UpdateAlarmInput uses the same fields but all are optional
  const updateInput: UpdateAlarmInput = {
    name: createInput.name,
    machineIds: createInput.machineIds,
    signal: createInput.signal,
    signalCategory: createInput.signalCategory,
    thresholdLow: createInput.thresholdLow,
    thresholdHigh: createInput.thresholdHigh,
    thresholdMode: createInput.thresholdMode,
    triggerMode: createInput.triggerMode,
    triggerValue: createInput.triggerValue,
    evalWindowSize: createInput.evalWindowSize,
    evalPeriod: createInput.evalPeriod,
    cooldownPeriod: createInput.cooldownPeriod,
  };

  return updateInput;
}

export function AddAlarmDialog({
  open,
  onOpenChange,
  selectedMachineIds,
  availableMachines = [],
  editingAlarm,
  onAlarmCreated,
}: AddAlarmDialogProps) {
  const isEditMode = !!editingAlarm;

  // Pulse only ever puts an alarm in one group, so the first entry is the group
  // this alert belongs to.
  const editingAlertGroupId = editingAlarm?.alertGroups?.[0]?.id ?? null;

  const { data: alertGroupData, loading: alertGroupLoading } =
    useGetAlertGroupQuery({
      variables: { id: editingAlertGroupId ?? '' },
      skip: !open || !editingAlertGroupId,
      fetchPolicy: 'network-only',
    });

  // AlertGroupAlarm carries only id, name and isTriggered, so the members' own
  // conditions have to come from the alarms list. EDMS scopes that list to the
  // signed-in user unless asked for everyone's, while the alarms table can show
  // every user's alerts. Without allUsers another user's group never resolves,
  // so the wizard fell back to the one alarm and showed no channels (bug 166139).
  const { data: memberAlarmsData, loading: memberAlarmsLoading } =
    useGetAlarmsQuery({
      variables: {
        filter: { allUsers: true },
        pagination: { limit: MEMBER_ALARM_FETCH_LIMIT },
      },
      skip: !open || !editingAlertGroupId,
      fetchPolicy: 'network-only',
    });

  const editingAlertGroup = alertGroupData?.alertGroup ?? null;

  const memberAlarms = useMemo((): Alarm[] => {
    if (!editingAlertGroup) return [];

    const alarmsById = new Map(
      (memberAlarmsData?.alarms.rows ?? []).map((alarm) => [alarm.id, alarm])
    );

    return (editingAlertGroup.alarms ?? [])
      .map((member) => alarmsById.get(member.id))
      .filter((alarm): alarm is Alarm => Boolean(alarm));
  }, [editingAlertGroup, memberAlarmsData]);

  const loadingAlertGroup =
    Boolean(editingAlertGroupId) && (alertGroupLoading || memberAlarmsLoading);

  // Both queries can settle without producing members: the group may hold none,
  // or they may sit outside the page of the alarms list read above. Editing the
  // group is not safe then - every signal would look
  // new and saving would rebuild the membership around duplicates - so fall back
  // to editing the one alarm that was clicked and leave the group alone.
  // Treating this as "still loading" instead leaves the dialog permanently
  // blank, with no spinner and no error.
  const groupMembersResolved =
    Boolean(editingAlertGroupId) &&
    !loadingAlertGroup &&
    memberAlarms.length > 0;

  const initialFormData = editingAlarm
    ? groupMembersResolved && editingAlertGroup
      ? convertAlertGroupToFormData(editingAlertGroup, memberAlarms)
      : convertAlarmToFormData(editingAlarm)
    : undefined;

  // Use a key to force React to remount the provider when switching between
  // create mode and edit mode, or when editing a different alarm.
  // In create mode, include open state to reset when dialog reopens.
  // This ensures the form state is properly reset with the correct initial data.
  // A grouped alert also remounts once its members arrive, so the prefill lands.
  const providerKey = editingAlarm
    ? `${editingAlarm.id}-${memberAlarms.map((member) => member.id).join(',')}`
    : `create-${open}-${selectedMachineIds.join(',')}`;

  // Mounting while the queries are still in flight would prefill the single
  // clicked alarm and let the user save that over the whole group.
  if (loadingAlertGroup) {
    return null;
  }

  return (
    <AddAlarmDialogProvider
      key={providerKey}
      selectedMachineIds={selectedMachineIds}
      availableMachines={availableMachines}
      initialFormData={initialFormData}
      isEditMode={isEditMode}
      editingAlarmId={editingAlarm?.id || null}
    >
      <AddAlarmDialogContent
        open={open}
        onOpenChange={onOpenChange}
        onAlarmCreated={onAlarmCreated}
        editingAlertGroupId={groupMembersResolved ? editingAlertGroupId : null}
        editingAlarmInGroup={Boolean(editingAlertGroupId)}
        memberAlarms={memberAlarms}
      />
    </AddAlarmDialogProvider>
  );
}

function AddAlarmDialogContent({
  open,
  onOpenChange,
  onAlarmCreated,
  editingAlertGroupId,
  editingAlarmInGroup,
  memberAlarms,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAlarmCreated?: (alarmId: string) => void;
  /** The group being edited, once its members resolved. */
  editingAlertGroupId: string | null;
  /** Whether the edited alarm belongs to a group at all, resolved or not. */
  editingAlarmInGroup: boolean;
  memberAlarms: Alarm[];
}) {
  const {
    currentStep,
    setCurrentStep,
    formData,
    setFormData,
    selectedMachineIds,
    advancedSettingsExpanded,
    isEditMode,
    editingAlarmId,
  } = useAddAlarmDialog();
  const { prefersMobileLayout } = useResponsive();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Saving runs several mutations in a row; this covers the whole run
  const [isSaving, setIsSaving] = useState(false);

  // Every alert saves as member alarms plus a group, also with one signal: EDMS
  // evaluates and notifies alert groups only. The members are created through
  // their own hook, so the dialog stays open until the group itself exists.
  const [createMemberAlarm] = useCreateAlarmMutation();
  // The group update reloads the lists while a dropped member still exists, so
  // each delete reloads them again or the table shows it as a standalone alert.
  const [deleteMemberAlarm] = useDeleteAlarmMutation({
    refetchQueries: ['GetAlarms', 'GetAlertGroups'],
    awaitRefetchQueries: true,
  });
  const [createAlertGroup, { loading: creatingAlertGroup }] =
    useCreateAlertGroupMutation({
      refetchQueries: ['GetAlarms', 'GetAlertGroups'],
      awaitRefetchQueries: true,
    });
  const [updateMemberAlarm] = useUpdateAlarmMutation();
  const [updateAlertGroup, { loading: updatingAlertGroup }] =
    useUpdateAlertGroupMutation({
      refetchQueries: ['GetAlarms', 'GetAlertGroups'],
      awaitRefetchQueries: true,
    });

  const [updateAlarm, { loading: updatingAlarm }] = useUpdateAlarmMutation({
    onCompleted: (data) => {
      logger.debug('Pulse alert updated successfully:', data.updateAlarm);
      handleClose();
    },
    onError: (error) => {
      logger.error('Error updating Pulse alert:', error);
      setSubmitError(friendlyAlarmSaveError(error));
    },
    refetchQueries: ['GetAlarms'],
    awaitRefetchQueries: true,
  });

  const isSubmitting =
    isSaving || updatingAlarm || creatingAlertGroup || updatingAlertGroup;
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    onOpenChange(false);
    setSubmitError(null);
    // Reset form after animation completes (only for create mode)
    if (!isEditMode) {
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
      }

      resetTimeoutRef.current = setTimeout(() => {
        resetTimeoutRef.current = null;
        setCurrentStep(1);
        setFormData({
          name: '',
          machines: selectedMachineIds,
          signals: [],
          category: '',
          signalUnit: '',
          threshold: '',
          operation: 'greater',
          triggerType: 'minimum',
          thresholdMax: '',
          selectedChannels: [],
          channelSearchQuery: '',
          channelTypeFilters: [],
          triggerLogicType: 'percentage',
          // Advanced settings defaults
          percentageThreshold: '75',
          evaluationWindow: '5',
          cooldownPeriod: '5',
          periodMinutes: '75',
          numberOfSpikes: '5',
          multiSignalSettings: {},
          signalMeta: {},
        });
      }, 300);
    }
  };

  /**
   * A grouped alert is one alarm per signal plus a group over them. EDMS has no
   * batch create, so a failure part way through leaves orphan alarms that carry
   * no channels and would never notify. Delete what was created instead.
   */
  const rollbackMemberAlarms = async (alarmIds: string[]) => {
    for (const id of alarmIds) {
      try {
        await deleteMemberAlarm({ variables: { id } });
      } catch (error) {
        logger.error('Failed to roll back member alarm:', { id, error });
      }
    }
  };

  const createGroupedAlert = async () => {
    const createdAlarmIds: string[] = [];

    try {
      for (const input of buildMemberInputs(formData)) {
        const result = await createMemberAlarm({ variables: { input } });
        const createdId = result.data?.createAlarm.id;
        if (!createdId) {
          throw new Error(`No alert was returned for signal ${input.signal}.`);
        }
        createdAlarmIds.push(createdId);
      }

      const groupResult = await createAlertGroup({
        variables: {
          input: {
            name: formData.name,
            alarmIds: createdAlarmIds,
            notificationChannelIds: formData.selectedChannels,
          },
        },
      });

      if (!groupResult.data?.createAlertGroup.id) {
        throw new Error('No alert group was returned.');
      }

      logger.debug('Grouped Pulse alert created:', {
        alertGroupId: groupResult.data.createAlertGroup.id,
        alarmIds: createdAlarmIds,
      });

      handleClose();
      onAlarmCreated?.(createdAlarmIds[0]);
    } catch (error) {
      await rollbackMemberAlarms(createdAlarmIds);
      throw error;
    }
  };

  /**
   * Apply the wizard back onto an existing group. Group-level edits go to the
   * group, each signal's condition goes to its own member alarm, a new signal
   * becomes a new member, and a dropped signal loses its member.
   */
  const updateGroupedAlert = async (alertGroupId: string) => {
    const membersBySignal = new Map(
      memberAlarms.map((member) => [member.signal, member])
    );
    const createdAlarmIds: string[] = [];

    try {
      // Membership is rebuilt in the order the signals are shown.
      const alarmIds: string[] = [];
      for (const signal of formData.signals) {
        const memberForm = buildMemberFormData(formData, signal);
        const existing = membersBySignal.get(signal);

        if (existing) {
          await updateMemberAlarm({
            variables: {
              id: existing.id,
              input: convertFormDataToUpdateInput(memberForm),
            },
          });
          alarmIds.push(existing.id);
          continue;
        }

        const result = await createMemberAlarm({
          variables: { input: convertFormDataToInput(memberForm) },
        });
        const createdId = result.data?.createAlarm.id;
        if (!createdId) {
          throw new Error(`No alert was returned for signal ${signal}.`);
        }
        createdAlarmIds.push(createdId);
        alarmIds.push(createdId);
      }

      await updateAlertGroup({
        variables: {
          id: alertGroupId,
          input: {
            name: formData.name,
            alarmIds,
            notificationChannelIds: formData.selectedChannels,
          },
        },
      });

      // Only drop the removed members once the group no longer references them.
      const removedMembers = memberAlarms.filter(
        (member) => !formData.signals.includes(member.signal)
      );
      await rollbackMemberAlarms(removedMembers.map((member) => member.id));

      logger.debug('Grouped Pulse alert updated:', {
        alertGroupId,
        alarmIds,
        removed: removedMembers.map((member) => member.id),
      });

      handleClose();
    } catch (error) {
      // The members that already existed keep their edits. Only the ones this
      // attempt created are removed, so a retry does not leave duplicates.
      await rollbackMemberAlarms(createdAlarmIds);
      throw error;
    }
  };

  /**
   * Apply the wizard onto one alarm outside a resolved group. An alarm with no
   * group yet gets one, channels included: a plain alarm never fires. An alarm
   * whose group did not resolve keeps its group untouched and saves as before.
   */
  const updateUngroupedAlert = async (alarmId: string) => {
    if (editingAlarmInGroup) {
      const input = convertFormDataToUpdateInput(formData);
      logger.debug('Updating Pulse alert with input:', input);
      await updateAlarm({ variables: { id: alarmId, input } });
      return;
    }

    await updateMemberAlarm({
      variables: {
        id: alarmId,
        input: convertFormDataToUpdateInput(
          buildMemberFormData(formData, formData.signals[0])
        ),
      },
    });
    await createAlertGroup({
      variables: {
        input: {
          name: formData.name,
          alarmIds: [alarmId],
          notificationChannelIds: formData.selectedChannels,
        },
      },
    });
    logger.debug('Ungrouped Pulse alert moved into a group:', { alarmId });
    handleClose();
  };

  const handleNext = async () => {
    const effectiveLastStep = getNumSteps(formData);
    setSubmitError(null);

    if (currentStep < effectiveLastStep) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step - submit the form
      setIsSaving(true);
      try {
        if (isEditMode && editingAlertGroupId) {
          await updateGroupedAlert(editingAlertGroupId);
        } else if (isEditMode && editingAlarmId) {
          await updateUngroupedAlert(editingAlarmId);
        } else {
          await createGroupedAlert();
        }
      } catch (error) {
        logger.error('Error in handleNext:', error);
        setSubmitError(friendlyAlarmSaveError(error));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedTriggerLogic = () => {
    if (formData.triggerLogicType === 'percentage') {
      if (advancedSettingsExpanded) {
        return (
          (formData.percentageThreshold?.trim() ?? '') !== '' &&
          (formData.evaluationWindow?.trim() ?? '') !== '' &&
          (formData.cooldownPeriod?.trim() ?? '') !== ''
        );
      }
      return true;
    }
    if (formData.triggerLogicType === 'period') {
      if (advancedSettingsExpanded) {
        return (
          (formData.periodMinutes?.trim() ?? '') !== '' &&
          (formData.cooldownPeriod?.trim() ?? '') !== ''
        );
      }
      return true;
    }
    if (formData.triggerLogicType === 'spike') {
      if (advancedSettingsExpanded) {
        return (
          (formData.numberOfSpikes?.trim() ?? '') !== '' &&
          (formData.evaluationWindow?.trim() ?? '') !== '' &&
          (formData.cooldownPeriod?.trim() ?? '') !== ''
        );
      }
      return true;
    }
    return true;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name.trim() !== '' && formData.machines.length > 0;
      case 2:
        return formData.selectedChannels.length > 0;
      case 3:
        return formData.signals.length > 0 && formData.category.trim() !== '';
      case 4:
        if (formData.signals.length > 1) {
          const multiSignalSettings = formData.multiSignalSettings || {};
          return formData.signals.every((signal) => {
            const settings = multiSignalSettings[signal];
            if (!settings) {
              return false;
            }

            return (
              hasValidThresholds(settings) &&
              hasValidTriggerLogic(
                settings,
                settings.advancedSettingsExpanded || false
              )
            );
          });
        }

        if (isMachineAlarmFormData(formData)) {
          return (formData.cooldownPeriod?.trim() ?? '') !== '';
        }

        return hasValidThresholds(formData);
      case 5:
        return canProceedTriggerLogic();
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <AlarmDetailsStep />;
      case 2:
        return <AlarmChannelsStep />;
      case 3:
        return <AlarmSignalStep />;
      case 4:
        return <AlarmConditionsStep />;
      case 5:
        return <AlarmTriggerLogicStep />;
      default:
        return null;
    }
  };

  if (!open) {
    return null;
  }

  const totalSteps = getNumSteps(formData);
  const isMultiSignalConditionsStep =
    currentStep === 4 && formData.signals.length > 1;
  const stepConfig = STEPS[currentStep - 1];
  const stepLabels = STEPS.slice(0, totalSteps).map((step) => step.title);
  const wizardTitle = isEditMode ? 'Edit Alert' : 'New Alert';
  const completeLabel = isSubmitting
    ? isEditMode
      ? 'Saving...'
      : 'Creating...'
    : isEditMode
      ? 'Save'
      : 'Create';

  return (
    <section
      className='fixed z-70 flex flex-col'
      style={
        prefersMobileLayout
          ? {
              top: 'var(--spacing-header-height-mobile)',
              left: 0,
              right: 0,
              bottom: 0,
            }
          : {
              top: 'var(--spacing-header-height)',
              left: 'var(--spacing-sidebar-width)',
              right: 0,
              bottom: 0,
            }
      }
    >
      <WizardLayout
        title={wizardTitle}
        stepLabels={stepLabels}
        currentStep={currentStep}
        completeLabel={completeLabel}
        nextDisabled={!canProceed() || isSubmitting}
        onClose={handleClose}
        onBack={handleBack}
        onNext={handleNext}
        onComplete={handleNext}
      >
        <div
          className='flex h-full w-full items-start justify-center p-6 md:p-8'
        >
          <div
            className='bg-surface-primary border-border-primary flex h-full
              w-full max-w-105 flex-col overflow-hidden rounded-xl border
              shadow-[0px_8px_20px_0px_rgba(0,0,0,0.08)]'
            style={{
              maxWidth: isMultiSignalConditionsStep
                ? 'min(1520px, calc(100vw - 96px))'
                : undefined,
            }}
          >
            {!isMultiSignalConditionsStep && (
              <div className='px-6 pt-4 pb-3'>
                <h2 className='size-h4 text-primary font-bold'>
                  {stepConfig.title}
                </h2>
              </div>
            )}
            {submitError && (
              <div className='px-6 pt-3'>
                <ErrorDisplay
                  error={{ message: submitError }}
                  entityName='Pulse alert'
                  title='The alert could not be saved'
                />
              </div>
            )}
            <div className='min-h-0 flex-1 overflow-y-auto'>{renderStep()}</div>
          </div>
        </div>
      </WizardLayout>
    </section>
  );
}
